import { useEffect, useRef, useState } from "react";
import axios from "axios";
import css from "./index.module.css";
import SEO from "@components/seo";
import MainContent from "@components/content";
import * as Types from "../../types";
import ChatMessage from "@components/chatMessage";

function ChatInput({
  value,
  onChange,
  onSend,
  onFile,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  onFile: (f: File) => void;
  disabled?: boolean;
}) {
  const fileRef = useRef<HTMLInputElement | null>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className={css.chatInputBar}>
      <input
        type="file"
        ref={fileRef}
        style={{ display: "none" }}
        accept="image/*"
        onChange={(e) => {
          if (e.target.files && e.target.files[0]) {
            onFile(e.target.files[0]);
            e.target.value = "";
          }
        }}
      />
      <textarea
        className={css.chatInputField}
        placeholder="Введите сообщение"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        rows={1}
        style={{ resize: "none" }}
      />
      <button
        className={css.chatInputFileBtn}
        type="button"
        onClick={() => fileRef.current?.click()}
        tabIndex={-1}
        disabled={disabled}
        aria-label="Отправить файл"
      >
        <i
          className="fi fi-rr-picture"
          style={{ position: "relative", top: 2 }}
        ></i>
      </button>
      <button
        className={css.chatInputSendBtn}
        onClick={onSend}
        disabled={disabled || !value.trim()}
        type="button"
        aria-label="Отправить"
      >
        <i
          className="fi fi-rr-paper-plane"
          style={{ position: "relative", top: 2 }}
        ></i>
      </button>
    </div>
  );
}

// =========================================================================================

export default function Chat() {
  const [msgs, setMsgs] = useState<Types.Message[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<NodeJS.Timeout | null>(null);
  const [msg, setMsg] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Автоматическая прокрутка к самому концу чата, как в мессенджерах
  useEffect(() => {
    if (messagesEndRef.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 50);
    }
  }, [msgs.length]);

  useEffect(() => {
    axios
      .get(`${import.meta.env.VITE_API_URL}/chat`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        setMsgs(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const connect = () => {
    const ws = new WebSocket(import.meta.env.VITE_API_WEBSOCKET);

    ws.onopen = () => {
      const token = localStorage.getItem("authToken");
      ws.send(JSON.stringify({ type: 0, jwt: token }));
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type == 0 && data.status == "Authorized") {
        setIsConnected(true);
        if (reconnectTimer.current) {
          clearTimeout(reconnectTimer.current);
        }
      } else if (data.type === 1) {
        setMsgs((prevMsgs) => [
          ...prevMsgs,
          {
            msgType: data?.msgType,
            from: data?.from,
            to: data?.to,
            msg: data?.msg,
            createdAt: data?.createdAt,
          },
        ]);
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      reconnect();
    };

    ws.onerror = () => {
      ws.close();
    };

    wsRef.current = ws;
  };

  const reconnect = () => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
    }
    reconnectTimer.current = setTimeout(() => {
      connect();
    }, 3000);
  };

  useEffect(() => {
    connect();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
    };
    // eslint-disable-next-line
  }, [import.meta.env.VITE_API_WEBSOCKET]);

  const handleFileUpload = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/uploadFile`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      const imageUrl = response.data.url;
      wsRef.current?.send(
        JSON.stringify({ type: 1, msgType: 1, msg: imageUrl })
      );
    } catch (error) {
      console.error("Ошибка загрузки файла:", error);
    }
  };

  const sendMessage = () => {
    if (wsRef.current && isConnected && msg.trim()) {
      wsRef.current.send(JSON.stringify({ type: 1, msg }));
      setMsg("");
    }
  };

  return (
    <MainContent header="Переписка со складом">
      <SEO
        title="Главная"
        description="Магазин"
        ogUrl="https://рыбныимир.рф/"
      />

      <div className={css.messages}>
        <div
          className={css.msgList}
          style={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {msgs.map((item, index) => (
            <ChatMessage
              key={index}
              item={item}
              isFromMe={item.to !== "admin"}
              renderMode="mobile"
            />
          ))}
          {msgs.length === 0 && (
            <a
              style={{
                color: "#888",
                textAlign: "center",
                margin: "2rem auto 0",
                display: "block",
              }}
            >
              Здесь пусто, напишите сюда что-нибудь...
            </a>
          )}
          <div ref={messagesEndRef} />
        </div>
        <ChatInput
          value={msg}
          onChange={setMsg}
          onSend={sendMessage}
          onFile={handleFileUpload}
          disabled={!isConnected}
        />
      </div>
    </MainContent>
  );
}
